import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useGitOfflineQueue } from './hooks/useGitOfflineQueue';
import { registerGitActions } from './services/git/gitActions';
import { useGitSyncStore } from './store/gitSyncStore';
import { startDbHealthMonitor } from './extensions/capabilities/dbHealthMonitor';
import { useVaultStore, onVaultLocked } from './store/vaultStore';
import { startVaultActivity } from './services/vaultActivity';
import { startUsageCounting } from './services/telemetry/bootstrap';
import { webGateState } from './platform/webGate';
import WebSignInPage from './components/web/WebSignInPage';
import { desktopGateState } from './platform/desktopGate';
import { useFtpStore } from './store/ftpStore';
import { invoke } from '@tauri-apps/api/core';
import { useChatRealtime } from './hooks/useChatRealtime';
import { useTaskbarBadge } from './hooks/useTaskbarBadge';
import './extensions/bootstrap';
import { useToast } from './components/ui/Toast';
import { useAuthStore } from './store/authStore';
import { usePreferencesStore } from './store/preferencesStore';
import { useUiModalStore, setActiveTab, setAuthModalOpen, setAuthModalMode } from './store/uiModalStore';
import { useSidebarWidth } from './hooks/useSidebarWidth';
import {
  hydrateCollectionsFromApi,
} from './utils/collectionFolders';
import { collectionService } from './services/collectionService';
import { clearAccountNamespace } from './services/storageNamespace';
import { useTeamsStore } from './store/teamsStore';
import { useTeams } from './hooks/useTeams';
import { useOrganizations } from './hooks/useOrganizations';
import {
  useTabsStore, selectCurrentRequestTab, selectCurrentSchemaTab, setActiveTabType,
  ensureTaskManagerHomeTab, ensureProjectsHomeTab,
} from './store/tabsStore';
import { tabsOfWorkspace, useDocWorkspaceKey } from './store/documentTabWorkspaces';
import {
  useCollectionsStore,
  setCollections,
} from './store/collectionsStore';
import { useSignedInServices } from './hooks/useSignedInServices';
import { useLocalStoragePersistence } from './hooks/useLocalStoragePersistence';
import { useWorkspaceRealtime } from './hooks/useWorkspaceRealtime';
import { useVariableHydration } from './hooks/useVariableHydration';
import { useDeepLinkActions } from './hooks/useDeepLinkActions';
import { useSharedRequestOpen } from './hooks/useSharedRequestOpen';
import { useRequestDraftOpen } from './hooks/useRequestDraftOpen';
import { useGitLinksHydration } from './hooks/useGitLinksHydration';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useShallow } from 'zustand/react/shallow';
import usePaletteShortcut from './components/palette/usePaletteShortcut';
import usePaletteSetup from './components/palette/usePaletteSetup';
import { useAppZoom } from './hooks/useAppZoom';
import { useWorkspaceFileWatcher } from './hooks/useWorkspaceFileWatcher';
import { useWorkspacePersistence } from './hooks/useWorkspacePersistence';
import { useSyncEngine } from './hooks/useSyncEngine';
import { useCloudItemsReconcile } from './hooks/useCloudItemsReconcile';
import { useTeamBoardsRealtime } from './hooks/useTeamBoardsRealtime';
import { useDeviceLocalCollections } from './hooks/useDeviceLocalCollections';
import { useTabPersistence } from './hooks/useTabPersistence';
import { useCollectionsLoader } from './hooks/useCollectionsLoader';
import { useWorkspaceBootstrap } from './hooks/useWorkspaceBootstrap';
import { useMcpBus } from './hooks/useMcpBus';
import { useClaimExpiry } from './hooks/useClaimExpiry';
import { useVariablesScopes } from './hooks/useVariablesScopes';
import { useImportExport } from './hooks/useImportExport';
import { useSidebarRename } from './hooks/useSidebarRename';
import { useAiActions } from './hooks/useAiActions';
import { useAiExtensionActions } from './hooks/useAiExtensionActions';
import { useAiWorkspaceActions } from './hooks/useAiWorkspaceActions';
import { useAiDiscoveryActions } from './hooks/useAiDiscoveryActions';
import { useTabs } from './hooks/useTabs';
import { useSchemas } from './hooks/useSchemas';
import { useCollections } from './hooks/useCollections';
import { useBulkCollectionActions } from './hooks/useBulkCollectionActions';
import { useWorkspaces } from './hooks/useWorkspaces';
import {
  useWorkspaceStore,
} from './store/workspaceStore';
import { isTauri } from './utils/tauri';
import { useNotificationStore } from './store/notificationStore';
import { isBackendWorkspace, isLocalWorkspace } from './utils/workspaceStorage';
import {
  dedupeCollectionRequestIds,
} from './utils/localIds';
import {
  loadWorkspaceSnapshot,
} from './services/workspaceStoreRouter';
import { useAppUpdates } from './hooks/useAppUpdates';
import { useDeepLinks } from './hooks/useDeepLinks';
import { useContentLinks } from './deepLinks/contentLinks';
import { useWindowChrome } from './hooks/useWindowChrome';
import { useTranslation } from 'react-i18next';
import { useTranslatedToast } from './utils/translatedToast';
import { requestSignOut } from './store/signOutStore';
import {
  resolveRequestTabCollectionId,
  tabMatchesRequest,
} from './utils/requestResolve';
import { folderAuthChainForTab } from './utils/requestFolderAuth';
import {
  useVariablesStore,
  setEnvironments, setSelectedEnvironment, setWorkspaceVariables,
  setGlobalVariables,
} from './store/variablesStore';
import { EXTENSION_PANEL_TAB_TYPES, EXTENSION_TAB_TYPES } from './components/appShell/appTabTypes';
import { renderApp } from './components/appShell/renderApp';

function App() {
  const { isAuthenticated, isInitialized, user, initializeAuth } = useAuthStore();
  const sidebarPosition = usePreferencesStore((s) => s.sidebarPosition);
  const showNavTooltips = usePreferencesStore((s) => s.showNavTooltips);
  const { sidebarWidth, isResizing: isSidebarResizing, handleResizeStart: handleSidebarResizeStart } =
    useSidebarWidth();
  // UI / modal-visibility state lives in uiModalStore. Values are read
  // reactively here (identical names so JSX/logic is unchanged); the matching
  // setters are imported as stable module-level functions (see uiModalStore).
  // Only the modal values App itself still reads (in effects/handlers/main
  // layout) are destructured; the rest are read by ModalStack from the store.
  // Shallow-selected: App re-renders only when one of THESE changes — not on
  // every modal flag (opening the command palette must not re-render the app;
  // palette PLAN §3.3 open budget).
  const {
    activeTab, commandPaletteTeamDetailId, collectionRunnerTarget, aiPanelOpen,
  } = useUiModalStore(useShallow((s) => ({
    activeTab: s.activeTab,
    commandPaletteTeamDetailId: s.commandPaletteTeamDetailId,
    collectionRunnerTarget: s.collectionRunnerTarget,
    aiPanelOpen: s.aiPanelOpen,
  })));

  // Toast hook (declared early so extracted hooks can receive toastT)
  const { showToast, toasts, removeToast } = useToast();
  const { t } = useTranslation();
  const toastT = useTranslatedToast(showToast);

  // One database health monitor for the whole app, not one per extension host.
  // It sweeps every live connection, so a second instance would just double the
  // pings and race its own redials.
  useEffect(() => startDbHealthMonitor(), []);

  // Vault auto-lock wiring, once for the whole app: reports user activity to
  // Rust's idle timer and runs the shared lock cleanup when Rust announces
  // `vault:locked` (services/vaultActivity.js).
  useEffect(() => startVaultActivity(), []);

  // Feature usage counting: identifier-free counts of WHICH features are used,
  // gated on the Settings toggle. See services/telemetry/index.js for the contract.
  useEffect(() => startUsageCounting(), []);

  // Initialize auth only once at the top level
  useEffect(() => {
    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: re-running would re-bootstrap the session on every auth store change
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
      const { success, error, warning, info } = useNotificationStore.getState();
      window.testNotifications = {
        success: (msg) => success(msg || 'Test success notification'),
        error: (msg) => error(msg || 'Test error notification'),
        warning: (msg) => warning(msg || 'Test warning notification'),
        info: (msg) => info(msg || 'Test info notification')
      };
    }
  }, []);

  // Redirect away from teams tab if not authenticated
  useEffect(() => {
    if (activeTab === 'teams' && !isAuthenticated) {
      setActiveTab('collections');
    }
  }, [activeTab, isAuthenticated]);

  // Collections, schemas tree, sidebar selection/edit state and history live in
  // collectionsStore (values read here; setters imported; refs → getState()).
  const {
    collections, schemas, selectedRequest, selectedCollection, selectedFolder, editingItem,
    editingSchemaItem, selectedSchemaEntry, sidebarFadeInIds, history,
  } = useCollectionsStore();

  // Repair duplicate local request ids / OpenAPI `{var}` URLs from prior AI batch creates.
  useEffect(() => {
    if (!collections.length) return;
    const repaired = dedupeCollectionRequestIds(collections);
    if (repaired !== collections) {
      setCollections(repaired);
    }
  }, [collections]);
  const [forkingWorkspaceId, setForkingWorkspaceId] = useState(null);
  // Git sync state
  const [gitSyncingWorkspaceId, setGitSyncingWorkspaceId] = useState(null);
  const [publishingWorkspaceId, setPublishingWorkspaceId] = useState(null);
  const requestBuilderRenameRef = useRef(null);
  const schemaEditorRenameRef = useRef(null);
  const requestBuilderLiveStateRef = useRef(null);
  const requestBuilderSendRef = useRef(null);

  const registerRequestBuilderLiveState = useCallback((fn) => {
    requestBuilderLiveStateRef.current = fn;
  }, []);

  const registerRequestBuilderSendHandler = useCallback((fn) => {
    requestBuilderSendRef.current = fn;
  }, []);

  const renameSnapshotRef = useRef(null);
  const renameSchemaSnapshotRef = useRef(null);
  const selectRequestInFlightRef = useRef(new Map());
  // Variable scopes live in variablesStore (values read reactively here; setters
  // imported as stable module functions). The former ref-mirrors are gone —
  // async readers use useVariablesStore.getState().
  const {
    environments, selectedEnvironment, workspaceVariables, globalVariables, collectionVariablesById,
  } = useVariablesStore();

  // Teams state lives in teamsStore (values read here; setters imported).
  const { teams, teamLibraryRefresh } = useTeamsStore();

  // Workspace state lives in workspaceStore (values read here; setters imported).
  // workspaceReloadNonce is bumped when re-selecting the current workspace so
  // collections reload even though the id is unchanged.
  const {
    workspaces,
    currentWorkspace,
    gitAccount,
  } = useWorkspaceStore();

  // Tab + editor-tab state lives in tabsStore (values read here; setters imported).
  const {
    requestTabs, collectionTabs, schemaTabs, dbManagerTabs, tabStripOrder,
    activeRequestTabId, activeCollectionTabId, activeSchemaTabId, activeDbManagerTabId, activeTabType,
    taskManagerTabs, activeTaskManagerTabId,
    projectsTabs, activeProjectsTabId,
    documentTabs, activeDocumentTabId,
  } = useTabsStore();

  // Collections/History and each extension panel (DB Manager, Task Manager)
  // own fully isolated tab strips — switching activeTab must never show one
  // context's tabs mixed into another's. `activeTab` holds the panel id
  // (ActivityBar.js's targetTabFor sets it to the panel id for extension
  // items, e.g. 'db-manager.panel') when an extension panel is selected;
  // this maps that id to the tab type it forces, so adding another extension
  // panel with its own tab strip is just another entry here.
  const extensionTabType = EXTENSION_PANEL_TAB_TYPES[activeTab] || null;
  const isExtensionContext = extensionTabType !== null;
  const lastNonExtensionTabTypeRef = useRef(EXTENSION_TAB_TYPES.has(activeTabType) ? 'request' : activeTabType);
  useEffect(() => {
    if (!EXTENSION_TAB_TYPES.has(activeTabType)) lastNonExtensionTabTypeRef.current = activeTabType;
  }, [activeTabType]);
  // activeTabType is a dep on purpose: things other than context switches can
  // flip it while an extension panel is active (login/workspace-switch tab
  // hydration sets it to 'request'), which would otherwise paint the request
  // builder into the extension's content area with no visible tab. Keeping the
  // coercion reactive to both makes it self-healing in either direction.
  useEffect(() => {
    if (isExtensionContext && activeTabType !== extensionTabType) {
      setActiveTabType(extensionTabType);
    } else if (!isExtensionContext && EXTENSION_TAB_TYPES.has(activeTabType)) {
      setActiveTabType(lastNonExtensionTabTypeRef.current || 'request');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExtensionContext, extensionTabType, activeTabType]);

  // Task Manager always needs its pinned "Boards" tab present before
  // EditorPane renders it — ensured (without stealing focus from an
  // already-active board tab) every time this panel becomes active.
  useEffect(() => {
    if (activeTab === 'task-manager.panel') ensureTaskManagerHomeTab();
    if (activeTab === 'projects.panel') ensureProjectsHomeTab();
  }, [activeTab]);

  // Tab strip contents/order handed to EnhancedTabManager, isolated per context
  // (see isExtensionContext above) so neither strip ever shows the other's tabs.
  const stripRequestTabs = isExtensionContext ? [] : requestTabs;
  const stripCollectionTabs = isExtensionContext ? [] : collectionTabs;
  const stripSchemaTabs = isExtensionContext ? [] : schemaTabs;
  const stripDbManagerTabs = extensionTabType === 'db-manager-table' ? dbManagerTabs : [];
  const stripTaskManagerTabs = extensionTabType === 'task-manager-board' ? taskManagerTabs : [];
  const stripProjectsTabs = extensionTabType === 'projects-project' ? projectsTabs : [];
  // Documents: only the tabs of the workspace the sidebar shows (one set per workspace).
  const docWorkspaceKey = useDocWorkspaceKey();
  const stripDocumentTabs = useMemo(
    () => (extensionTabType === 'document-editor' ? tabsOfWorkspace(documentTabs, docWorkspaceKey) : []),
    [extensionTabType, documentTabs, docWorkspaceKey]
  );
  const stripTabStripOrder = useMemo(() => {
    const docIds = new Set(stripDocumentTabs.map((tab) => tab.id));
    return tabStripOrder.filter((entry) =>
      isExtensionContext
        ? entry.type === extensionTabType && (entry.type !== 'document-editor' || docIds.has(entry.id))
        : !EXTENSION_TAB_TYPES.has(entry.type)
    );
  }, [tabStripOrder, isExtensionContext, extensionTabType, stripDocumentTabs]);

  const initializedRef = useRef(false);
  const prevWorkspaceIdForTabsRef = useRef(null);
  const prevWorkspaceIdForPersistRef = useRef(null);
  const tabsHydratingRef = useRef(false);
  const persistTabsTimeoutRef = useRef(null);
  const workspaceTabStateRef = useRef({});
  const snapshotHydratedRef = useRef(null);

  // Tab lifecycle + persistence handlers (state in tabsStore; effects below in App
  // share the gating refs so a workspace switch doesn't bleed tabs across workspaces).
  const {
    createNewRequestTab, openCollectionTab, openSchemaTab, getCurrentRequestTab,
    updateCurrentRequestTabStable, updateCurrentRequestTabResponse, clearTabLoading,
    closeTab, duplicateTab, closeOtherTabs, closeAllTabs,
    flushWorkspaceTabPersist, scheduleWorkspaceTabPersist, handleTabsReorder,
    restoreWorkspaceTabs, handleTabChange, handleNewRequestTab,
  } = useTabs({ persistTabsTimeoutRef, workspaceTabStateRef, initializedRef, tabsHydratingRef });

  // Schema tree CRUD + schema-tab editing + schema persistence (snapshot + YAML mirror).
  const {
    handleRenameSchemaEntry, handleSelectSchemaEntry, handleStartSchemaRename,
    handleSaveSchemaRename, handleCancelSchemaRename, handleCommitSchemaName,
    handleDeleteSchemaEntry, handleMoveSchemaEntry, buildLiveSnapshot,
    persistSchemas, handleCreateSchema, handleCreateSchemaFolder,
    handleDuplicateSchema, updateCurrentSchemaTabStable, saveSchemaTab,
  } = useSchemas({ openSchemaTab, toastT, snapshotHydratedRef, renameSchemaSnapshotRef });

  // Workspace lifecycle handlers (leaf ops in brief-05a: git/clone/publish/fork).
  // Effects (load/mirror/file-watcher/splash) + loading-state useState stay in App.
  const {
    canAccessWorkspace, reloadWorkspacesFromServer, switchWorkspace, handleOpenWorkspaceModal,
    handleWorkspaceCreated, handleWorkspaceUpdated, handleWorkspaceEdit, handleWorkspaceDelete,
    handleGitSync, handleGitPull, handleGitChanges, handleGitPutBack, handleGitItemHistory,
    handleGitConnectConfirm, handleGitAddRemote, handleGitStopTracking, handleGitResolveConflicts,
    handleWorkspaceCloned, requestPublishLocalWorkspace, handlePublishLocalConfirm,
    canPublishToNetwork, requestPublishToNetwork, handleNetworkImported, openForkWorkspaceModal,
    handleForkWorkspaceConfirm,
  } = useWorkspaces({
    toastT, buildLiveSnapshot, snapshotHydratedRef, setGitSyncingWorkspaceId,
    setPublishingWorkspaceId, setForkingWorkspaceId,
  });
  // Syncs that waited for the network go out when it is back (git card 7, set 7d).
  useGitOfflineQueue(handleGitSync);
  // The request editor's History button reaches these without prop drilling (card 9).
  useEffect(() => registerGitActions({ itemHistory: handleGitItemHistory, putBack: handleGitPutBack }), [handleGitItemHistory, handleGitPutBack]);
  // ...and can see when a sync is running (it must not rewrite the mirror then).
  useEffect(() => useGitSyncStore.setState({ syncingId: gitSyncingWorkspaceId ?? null }), [gitSyncingWorkspaceId]);

  const getLiveEditorStateForRequest = useCallback((requestId) => {
    const read = requestBuilderLiveStateRef.current;
    if (!read || requestId == null) return null;
    const activeTab = useTabsStore.getState().requestTabs.find(
      (t) => String(t.id) === String(useTabsStore.getState().activeRequestTabId)
    );
    if (!activeTab || !tabMatchesRequest(activeTab, requestId)) {
      return null;
    }
    return read() ?? null;
  }, []);
  

  // Memoize the current tabs to prevent unnecessary re-renders
  const currentRequestTab = useTabsStore(selectCurrentRequestTab);
  const currentSchemaTab = useTabsStore(selectCurrentSchemaTab);
  const currentCollectionVariables = useMemo(() => {
    const cid = resolveRequestTabCollectionId(currentRequestTab, collections);
    if (!cid) return [];
    return collectionVariablesById[cid] || [];
  }, [collectionVariablesById, currentRequestTab, collections]);

  const requestCollectionName = useMemo(() => {
    const cid = resolveRequestTabCollectionId(currentRequestTab, collections);
    if (!cid) return null;
    return collections.find((c) => String(c.id) === String(cid))?.name ?? null;
  }, [collections, currentRequestTab]);

  const requestCollectionAuth = useMemo(() => {
    const cid = resolveRequestTabCollectionId(currentRequestTab, collections);
    if (!cid) return null;
    const collection = collections.find((c) => String(c.id) === String(cid));
    return collection?.settings?.collection_auth ?? null;
  }, [collections, currentRequestTab]);

  const requestFolderAuthChain = useMemo(
    () => (currentRequestTab ? folderAuthChainForTab(currentRequestTab, collections) : []),
    [collections, currentRequestTab]
  );

  // Tab persistence + hydration (backfill/heal/save-restore-on-switch/persist/
  // beforeunload/collection-sync). Refs are App-owned (shared with useTabs +
  // onLogoutReset); useTabs handlers passed in.
  useTabPersistence({
    workspaceTabStateRef, tabsHydratingRef, initializedRef, prevWorkspaceIdForTabsRef,
    persistTabsTimeoutRef, prevWorkspaceIdForPersistRef, restoreWorkspaceTabs, closeAllTabs,
    scheduleWorkspaceTabPersist, flushWorkspaceTabPersist,
  });

  // Global keyboard shortcuts (rename/new-tab/command-palette/ai/devtools +
  // duplicate) live in useKeyboardShortcuts — called below once duplicate*
  // handlers exist; it returns handleSidebarDuplicate for the Sidebar.

  // Modal states
  const collectionRunnerTargetRef = useRef(null);
  const collectionRunnerAiResolveRef = useRef(null);
  collectionRunnerTargetRef.current = collectionRunnerTarget;

  // Pending deep-link tokens (team-invite / network-import) + Stripe billing
  // return + can-publish entitlement. Extracted to a hook; reads stores reactively.
  useDeepLinkActions({ showToast });

  // Play on a shared-request chat card → open it as an unsaved builder tab.
  useSharedRequestOpen({ createNewRequestTab, showToast });
  useRequestDraftOpen({ createNewRequestTab }); // "Open as request" from the terminal

  // App auto-update lifecycle (state in updateStore, effects/handlers in hook).
  const {
    updateInfo, updateModalOpen, updateInstalling, updateError, onUpdateModalClose,
    handleOpenUpdateModal, handleManualUpdateCheck, handleUpdateLater, handleSkipVersion,
    handleInstallUpdate,
  } = useAppUpdates({ showToast, t });

  // Tauri deep-link listeners → uiModalStore.
  useDeepLinks({ showToast });

  // `auth:logout` can fire more than once per real logout (authStore's own
  // dispatch plus apiClient's 401 interceptor reacting to any stale in-flight
  // request) — a second reset call re-arms initialLoadComplete/workspaceLoadKeyRef
  // after the workspace-load effect already finished, and since isAuthenticated
  // doesn't change again the effect never reruns, leaving the splash stuck forever.
  // Only honor the first reset per logout; re-arm once the user signs in again.
  const loggedOutResetHandledRef = useRef(false);
  useEffect(() => {
    if (isAuthenticated) loggedOutResetHandledRef.current = false;
  }, [isAuthenticated]);

  // Clear App-owned state on logout (UI/update stores are reset inside the hook).
  // Shrinks as remaining domains move to their own stores (Phases 4–8).
  const onLogoutReset = useCallback(() => {
    if (loggedOutResetHandledRef.current) return;
    loggedOutResetHandledRef.current = true;
    useCollectionsStore.getState().reset();
    useVariablesStore.getState().reset();
    useTeamsStore.getState().reset();
    // One teardown for every account-owned browser key (teams caches included)
    // — see services/storageNamespace.js. Idempotent: authStore.logout() has
    // usually run it already, in which case the namespace is `_local` and this
    // is a no-op that leaves signed-out local-first data alone.
    clearAccountNamespace();
    useWorkspaceStore.getState().reset();
    useTabsStore.getState().reset();
    // Vault: the shared lock teardown first (drops the in-flight unlock and
    // the module-level E2E keypair cache — vaultStore.onVaultLocked, the same
    // body the Lock button and the `vault:locked` event run, so logout can
    // never drift into a weaker cleanup than either), then reset() wipes the
    // store to its signed-out defaults for the next account.
    onVaultLocked();
    useVaultStore.getState().reset();
    if (isTauri()) {
      try {
        invoke('vault_lock').catch(() => {});
      } catch {
        // ignore — vault commands may not exist in this build
      }
    }
    // FTP: clear listings/transfers and best-effort close every session.
    useFtpStore.getState().reset();
    if (isTauri()) {
      try {
        invoke('ftp_disconnect_all').catch(() => {});
      } catch {
        // ignore — ftp commands may not exist in this build
      }
    }
    initializedRef.current = false;
    setInitialLoadComplete(false);
    workspaceLoadKeyRef.current = '';
    collectionsLoadingRef.current = false;
    snapshotHydratedRef.current = null;
    setIsLoadingCollections(false);
    setHasLoadedCollectionsAfterAuth(false);
    // Open tabs are NOT wiped from here any more. They live under the account
    // root now (services/workspaceTabStore.js), which sign-out deletes whole
    // (accountData.purgeAccountData); and by the time this reset runs the
    // namespace has already flipped to `_local`, so a "clear the current
    // account's tabs" call here would delete the signed-out bucket's tabs
    // rather than the outgoing account's.
    prevWorkspaceIdForTabsRef.current = null;
    prevWorkspaceIdForPersistRef.current = null;
  }, []);

  useWindowChrome({ onLogoutReset });

  // Loading states
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isLoadingCollections, setIsLoadingCollections] = useState(false);
  const collectionsLoadingRef = useRef(false);
  const [hasLoadedCollectionsAfterAuth, setHasLoadedCollectionsAfterAuth] = useState(false);
  const workspaceLoadKeyRef = useRef('');

  // App boot subsystem: splash/wsTransition machine + workspaces-load + guest→login
  // merge. Loading state stays App-owned (shared w/ useCollectionsLoader + onLogoutReset).
  const { splashVisible, splashFading } = useWorkspaceBootstrap({
    toastT, initialLoadComplete, setInitialLoadComplete, workspaceLoadKeyRef, isLoadingCollections,
  });

  // The MCP server's JS half. Mounted app-wide, never from the settings modal:
  // an external client can call a tool at any moment (.ai/tasks/2026-09-09_mcp_server).
  useMcpBus();

  // Plans v2 card 2.4: offline past the signed plan claim's 14 days, the
  // desktop gate below requires sign-in again (nothing is deleted).
  const claimExpiry = useClaimExpiry();

  // apiboo://documents|boards/… links (content_link.rs): received from the
  // first render, opened only once signed in and past the desktop gate.
  const signedInReady = isAuthenticated && desktopGateState({ isInitialized, isAuthenticated, claim: claimExpiry }) === 'app';
  useContentLinks({ ready: signedInReady });
  // Calendar reminders, mail catcher, mock-server sync: only while signed in
  // past the gate; stopped again on sign-out (hooks/useSignedInServices).
  useSignedInServices({ active: signedInReady });

  // Hydrate workspace-scoped + collection-scoped variables from their payloads.
  // Extracted to a hook (same effects/deps/position → effect order unchanged).
  useVariableHydration();

  // Collections (+ schemas + variable scopes) loader for the current workspace.
  // Loading state stays App-owned (shared with workspaces-load + splash); passed in.
  useCollectionsLoader({
    initialLoadComplete, isLoadingCollections, hasLoadedCollectionsAfterAuth,
    setIsLoadingCollections, setHasLoadedCollectionsAfterAuth, collectionsLoadingRef,
    snapshotHydratedRef, canAccessWorkspace, toastT,
  });

  // Device-local collection snapshots (link badges) + on-disk workspace manifest.
  const { reloadDeviceLocalCollections } = useDeviceLocalCollections();

  // Debounced workspace-persistence writers (local snapshot save + YAML mirror +
  // cloud schema persist), all gated on snapshotHydratedRef. Extracted to a hook.
  useWorkspacePersistence({ buildLiveSnapshot, persistSchemas, snapshotHydratedRef });
  useSyncEngine();
  // Plans v2 card 3.4: cloud items of a plan without cloud sync.
  useCloudItemsReconcile();
  useTeamBoardsRealtime();

  // Hydrate persisted git remote links onto workspaces once after they load.
  useGitLinksHydration();

  // Git-workspace file watcher (Tauri): reloads snapshot on external change.
  useWorkspaceFileWatcher({ toastT });

  // localStorage caches (collections/history/env/globals/workspaces) mirror store
  // state to disk. Extracted to a hook; reads everything reactively from stores.
  useLocalStoragePersistence();

  // Collection/folder/request domain handlers (built up across brief-04 increments).
  const {
    selectRequest, saveRequest, handleSelectCollectionForPendingRequest,
    handleCollectionTabDataChange, saveCollectionFromTab, scheduleTreeSave, handleCreateCollection,
    createRequest, deleteRequest, deleteCollection, handleCreateFolder, createFolderReturningId,
    handleDeleteFolder, handleSidebarCollectionsReorder, duplicateRequest, duplicateCollection,
    duplicateFolder, createCollection, openImportCollection, openExportCollection,
    openRunCollection, openCodeGen, handleCollectionRunnerClose, handleCollectionRunnerComplete,
    getExportableCollections, openExportCollectionFromPalette, handleSelectCollectionForExport,
    selectFolder, selectFromHistory, addToHistory, clearHistory, handleClearHistory,
    clearHistoryForRequest,
  } = useCollections({
    toastT, showToast, createNewRequestTab, updateCurrentRequestTabStable, getCurrentRequestTab,
    getLiveEditorStateForRequest, reloadDeviceLocalCollections, selectRequestInFlightRef,
    collectionRunnerTargetRef, collectionRunnerAiResolveRef,
  });

  // Bulk actions for a multi-row sidebar selection. Wraps the SAME single-item
  // deletes above with `{ silent: true }` so a 50-row triage emits one summary
  // toast instead of fifty.
  const {
    bulkDelete: handleBulkDelete,
    bulkDuplicate: handleBulkDuplicate,
  } = useBulkCollectionActions({
    deleteRequest,
    deleteCollection,
    deleteFolder: handleDeleteFolder,
    duplicateRequest, duplicateCollection, duplicateFolder, toastT,
  });

  // Reveal/open/export workspaces + collection import (Tauri paths early-return in browser).
  const {
    handleRevealInExplorer, handleOpenGitWorkspace, handleExportWorkspaceToFolder,
    handleImportCollections, handleImportEnvironment, handleDroppedImportFile,
  } = useImportExport({ toastT, snapshotHydratedRef });

  // Teams handlers
  const {
    handleTeamsModal, handleTeamCreated, handleTeamUpdated, handleOpenTeamFromCommandPalette,
    clearCommandPaletteTeamDetail, handleTeamJoined, handleShareWithTeamSuccess,
  } = useTeams({ toastT, isAuthenticated, reloadWorkspacesFromServer });

  // Organisations load beside teams, not per component: several surfaces read
  // the list and it changes about once a year.
  useOrganizations({ isAuthenticated });

  const openAuthModal = useCallback((mode = 'login') => {
    setAuthModalMode(mode);
    setAuthModalOpen(true);
  }, []);

  const handleSignOut = useCallback(async () => {
    // An explicit sign-out: the only kind that deletes this account's local
    // data — and asks first when some of it exists only on this device.
    await requestSignOut();
  }, []);

  // Variable-scope persistence (Environments modal + inline editor upserts).
  const { saveVariableScope, handleVariableUpdate } = useVariablesScopes({
    toastT,
    snapshotHydratedRef,
  });

  // AI assistant tool handlers + request-builder send/live-state bridge reads.
  const {
    aiCreateCollection, aiCreateFolders, aiCreateRequest, aiMoveRequests, aiExecuteRequest,
    aiRunCollection, getCurrentEditorRequest, aiGetLastResponse, aiGenerateTests,
    aiGeneratePreRequestScript, aiUpdateRequest,
  } = useAiActions({
    scheduleTreeSave, createNewRequestTab, selectRequest, requestBuilderSendRef,
    requestBuilderLiveStateRef, collectionRunnerAiResolveRef,
  });

  // AI tool handlers for the bundled extensions (Task Manager / DB Manager / Documents).
  const aiExtensionActions = useAiExtensionActions();
  // …and for the core workspace tools added 18.08.2026 (history, environment
  // lifecycle, auth). They ride the same handler-map dispatch in AIChatPanel.
  // generate_code reads the builder's live draft so a snippet matches what is
  // on screen, not the last committed tab record. Stable identity on purpose:
  // an inline arrow here is a new dependency every render, which rebuilds the
  // whole AI handler map and re-renders the chat panel on every keystroke.
  const readLiveRequestState = useCallback(
    () => requestBuilderLiveStateRef.current?.() ?? null,
    []
  );

  const aiWorkspaceActions = useAiWorkspaceActions({
    onUpdateRequest: aiUpdateRequest,
    onImportCollections: handleImportCollections,
    onCreateRequest: aiCreateRequest,
    readLiveRequestState: readLiveRequestState,
  });
  // Web discovery (web_search via our API, fetch_url via the Rust client) —
  // same handler-map dispatch, no workspace state.
  const aiDiscoveryActions = useAiDiscoveryActions();
  const aiToolActions = useMemo(
    () => ({ ...aiExtensionActions, ...aiWorkspaceActions, ...aiDiscoveryActions }),
    [aiExtensionActions, aiWorkspaceActions, aiDiscoveryActions]
  );

  // Ctrl/Cmd + wheel and Ctrl/Cmd +/-/0, the browser gesture this window has
  // no browser chrome to provide — remembered per rail page (activeTab).
  useAppZoom(activeTab);

  // Command palette v2: ⌘K / Ctrl+K and ⌘P from everywhere (window capture),
  // never on the sign-in pages (PLAN D5); providers register once past the gate.
  const paletteGated = webGateState({ isInitialized, isAuthenticated }) !== 'app'
    || desktopGateState({ isInitialized, isAuthenticated, claim: claimExpiry }) !== 'app';
  usePaletteShortcut({ gated: paletteGated });
  usePaletteSetup({ ready: !paletteGated });

  const { handleSidebarDuplicate } = useKeyboardShortcuts({
    handleStartSchemaRename, handleNewRequestTab, renameSnapshotRef, schemaEditorRenameRef,
    requestBuilderRenameRef, duplicateRequest, duplicateCollection, duplicateFolder,
    handleDuplicateSchema,
  });

  // Sidebar/editor rename handlers (request/collection/folder name editing).
  const {
    handleStartSidebarRename, handleSidebarRenameDraft, handleCommitRequestName,
    handleSaveSidebarRename, handleCancelSidebarRename,
  } = useSidebarRename({ toastT, renameSnapshotRef, scheduleTreeSave });

  const refreshCollections = async () => {
    if (!currentWorkspace) return;
    try {
      if (isBackendWorkspace(currentWorkspace, isAuthenticated)) {
        const workspaceCollections = await collectionService.getCollections(currentWorkspace.id);
        setCollections(hydrateCollectionsFromApi(workspaceCollections));
        await reloadDeviceLocalCollections();
        toastT('toast.collectionsRefreshed', 'success');
      } else if (isLocalWorkspace(currentWorkspace, isAuthenticated)) {
        snapshotHydratedRef.current = null;
        const snap = await loadWorkspaceSnapshot(currentWorkspace);
        setCollections(snap.collections || []);
        setGlobalVariables(snap.globalVariables || []);
        setWorkspaceVariables(snap.workspaceVariables || []);
        const envs = snap.environments || [];
        setEnvironments(envs);
        if (snap.selectedEnvironmentId) {
          const sel = envs.find((e) => String(e.id) === String(snap.selectedEnvironmentId));
          setSelectedEnvironment(sel || null);
        } else {
          setSelectedEnvironment(null);
        }
        snapshotHydratedRef.current = currentWorkspace.id;
        toastT('toast.localCollectionsRefreshed', 'success');
      }
    } catch (error) {
      console.error('Failed to refresh collections:', error);
      toastT('toast.refreshCollectionsError', 'error', { message: error.message });
    }
  };

  const workspaceReadOnly =
    isBackendWorkspace(currentWorkspace, isAuthenticated) &&
    currentWorkspace?.type === 'team' &&
    currentWorkspace?.can_edit === false;

  // Realtime team-workspace channel: applies remote broadcasts to local
  // collections (no per-event refetch). Extracted to a hook; reads stores reactively.
  useWorkspaceRealtime();

  // Chat realtime: keeps the conversation list + unread badges live (always on)
  // and streams the active conversation's messages/typing/presence.
  useChatRealtime();

  // Reflects the combined chat + notification unread count on the desktop app
  // icon (taskbar overlay / Dock badge).
  useTaskbarBadge();

  // web.apiboo.com: sign-in IS the page until there is a session (platform/webGate.js).
  // After every hook above, so the hook order is the same on both branches.
  const webGate = webGateState({ isInitialized, isAuthenticated });
  if (webGate !== 'app') return <WebSignInPage loading={webGate === 'loading'} />;
  // Plans v2 (2.1): the desktop app needs a session too. Unlike the web page
  // the title bar stays, so the window can still be moved and closed.
  const desktopGate = desktopGateState({ isInitialized, isAuthenticated, claim: claimExpiry });

  return renderApp({
    t, desktopGate, teams, collections, isAuthenticated, user, sidebarPosition, showNavTooltips,
    sidebarWidth, isSidebarResizing, handleSidebarResizeStart, activeTab,
    commandPaletteTeamDetailId, aiPanelOpen, showToast, toasts, removeToast, schemas, history,
    selectedRequest, selectedCollection, selectedFolder, editingItem, editingSchemaItem,
    selectedSchemaEntry, sidebarFadeInIds, forkingWorkspaceId, gitSyncingWorkspaceId,
    publishingWorkspaceId, requestBuilderRenameRef, schemaEditorRenameRef,
    registerRequestBuilderLiveState, registerRequestBuilderSendHandler, selectRequest, environments,
    selectedEnvironment, workspaceVariables, globalVariables, collectionVariablesById,
    teamLibraryRefresh, workspaces, currentWorkspace, gitAccount, requestTabs, collectionTabs,
    schemaTabs, activeRequestTabId, activeCollectionTabId, activeSchemaTabId, activeDbManagerTabId,
    activeTabType, activeTaskManagerTabId, activeProjectsTabId, activeDocumentTabId,
    extensionTabType, isExtensionContext, stripRequestTabs, stripCollectionTabs, stripSchemaTabs,
    stripDbManagerTabs, stripTaskManagerTabs, stripProjectsTabs, stripDocumentTabs,
    stripTabStripOrder, openCollectionTab, openSchemaTab, updateCurrentRequestTabStable,
    updateCurrentRequestTabResponse, clearTabLoading, closeTab, duplicateTab, closeOtherTabs,
    closeAllTabs, handleTabsReorder, handleTabChange, handleNewRequestTab, handleRenameSchemaEntry,
    handleSelectSchemaEntry, handleStartSchemaRename, handleSaveSchemaRename,
    handleCancelSchemaRename, handleCommitSchemaName, handleDeleteSchemaEntry,
    handleMoveSchemaEntry, handleCreateSchema, handleCreateSchemaFolder, handleDuplicateSchema,
    updateCurrentSchemaTabStable, saveSchemaTab, reloadWorkspacesFromServer, switchWorkspace,
    handleOpenWorkspaceModal, handleWorkspaceCreated, handleWorkspaceUpdated, handleWorkspaceEdit,
    handleWorkspaceDelete, handleGitSync, handleGitPull, handleGitChanges, handleGitPutBack,
    handleGitConnectConfirm, handleGitAddRemote, handleGitStopTracking, handleGitResolveConflicts,
    handleWorkspaceCloned, requestPublishLocalWorkspace, handlePublishLocalConfirm,
    canPublishToNetwork, requestPublishToNetwork, handleNetworkImported, openForkWorkspaceModal,
    handleForkWorkspaceConfirm, currentSchemaTab, currentCollectionVariables, requestCollectionName,
    requestCollectionAuth, requestFolderAuthChain, handleSidebarDuplicate, updateInfo,
    updateModalOpen, updateInstalling, updateError, onUpdateModalClose, handleOpenUpdateModal,
    handleManualUpdateCheck, handleUpdateLater, handleSkipVersion, handleInstallUpdate,
    splashVisible, splashFading, claimExpiry, saveRequest, handleSelectCollectionForPendingRequest,
    handleCollectionTabDataChange, saveCollectionFromTab, handleCreateCollection, createRequest,
    deleteRequest, deleteCollection, handleCreateFolder, createFolderReturningId,
    handleDeleteFolder, handleSidebarCollectionsReorder, createCollection, openImportCollection,
    openExportCollection, openRunCollection, openCodeGen, handleCollectionRunnerClose,
    handleCollectionRunnerComplete, getExportableCollections, openExportCollectionFromPalette,
    handleSelectCollectionForExport, selectFolder, selectFromHistory, addToHistory, clearHistory,
    handleClearHistory, clearHistoryForRequest, handleBulkDelete, handleBulkDuplicate,
    handleRevealInExplorer, handleOpenGitWorkspace, handleExportWorkspaceToFolder,
    handleImportCollections, handleImportEnvironment, handleDroppedImportFile, handleTeamsModal,
    handleTeamCreated, handleTeamUpdated, handleOpenTeamFromCommandPalette,
    clearCommandPaletteTeamDetail, handleTeamJoined, handleShareWithTeamSuccess, openAuthModal,
    handleSignOut, saveVariableScope, handleVariableUpdate, aiCreateCollection, aiCreateFolders,
    aiCreateRequest, aiMoveRequests, aiExecuteRequest, aiRunCollection, getCurrentEditorRequest,
    aiGetLastResponse, aiGenerateTests, aiGeneratePreRequestScript, aiUpdateRequest, aiToolActions,
    handleStartSidebarRename, handleSidebarRenameDraft, handleCommitRequestName,
    handleSaveSidebarRename, handleCancelSidebarRename, refreshCollections, workspaceReadOnly,
  });
}

export default App;
